import { ProductListingProduct } from "@/types/productListing";
import { PriceRange } from "@~/types/prodcatBaseTypes";

type DisplayPrice = {
  min: string;
  max: string;
};

export const mapProduct = (product: ProductListingProduct, index: number) => {
  const promoMessages =
    product.messaging && product.messaging.length > 0
      ? product.messaging.filter(({ type }) => type === "promotional")
      : [];

  const stripCurrencySymbol = (price: string) =>
    price ? price.replace(/[£,]/g, "").trim() : "";

  const stripComma = (price: string) =>
    price ? price.replace(/,/g, "").trim() : "";

  const formatPrice = (displayPrice: DisplayPrice) => {
    const { min, max } = displayPrice;
    if (min === max) return stripComma(stripCurrencySymbol(min));
    return {
      from: stripComma(stripCurrencySymbol(min)),
      to: stripComma(stripCurrencySymbol(max)),
    };
  };

  const formatPrices = (variantPriceRange: PriceRange) => {
    const priceNow = variantPriceRange?.display
      ? formatPrice(variantPriceRange.display)
      : "";

    const { reductionHistory = [] } = variantPriceRange || {};
    const numPreviousPrices = reductionHistory.length;

    if (numPreviousPrices === 0) {
      return { priceNow, priceThen: "", priceWas: "" };
    }

    // Sort by chronology descending to get newest first
    const sortedHistory = [...reductionHistory].sort(
      (a, b) => b.chronology - a.chronology,
    );

    if (numPreviousPrices === 1) {
      return {
        priceNow,
        priceThen: "",
        priceWas: formatPrice(sortedHistory[0]!.display),
      };
    }

    if (numPreviousPrices === 2) {
      return {
        priceNow,
        priceThen: formatPrice(sortedHistory[0]!.display), // newest
        priceWas: formatPrice(sortedHistory[1]!.display), // oldest
      };
    }

    // numPreviousPrices > 2
    return {
      priceNow,
      priceThen: `${formatPrice(sortedHistory[1]!.display)}, ${formatPrice(
        sortedHistory[0]!.display,
      )}`,
      priceWas: formatPrice(sortedHistory[2]!.display),
    };
  };

  const { priceNow, priceThen, priceWas } = formatPrices(
    product.variantPriceRange,
  );

  const citrusAdId =
    product.advertising?.associatedAdvertId && !product.advertising?.clickUrl
      ? product.advertising.associatedAdvertId
      : undefined;
  const kevelAdId =
    product.advertising?.associatedAdvertId && product.advertising?.clickUrl
      ? product.advertising.associatedAdvertId
      : undefined;

  return {
    ...(citrusAdId && { citrusAdId }),
    ...(kevelAdId && { kevelAdId }),
    currency: "gbp",
    isMultiSku: product.multiSku,
    isOutOfStock: product.outOfStock,
    listPosition: index + 1,
    mb: product.isMerchBoost ? 1 : 0,
    numColourSwatches: Math.min(10, product.colorSwatches?.length),
    numReviews: product.reviews,
    priceNow,
    priceThen,
    priceWas,
    productId: product.productId,
    ...(promoMessages.length > 0 && {
      promotionClassifications: promoMessages.map(
        ({ promotionGroup, promotionType }) => {
          return {
            group: promotionGroup,
            type: promotionType,
          };
        },
      ),
    }),
    rating: product.averageRating,
    sku: product.defaultSkuId,
  };
};
